'use client'

import { useState, useCallback, useEffect } from 'react'

const specData: Record<string, { dur: number; mpc: number; fee: number }> = {
  ortho:  { dur: 30, mpc: 20, fee: 195 },
  rheum:  { dur: 37, mpc: 20, fee: 195 },
  sleep:  { dur: 75, mpc: 30, fee: 200 },
  gastro: { dur: 30, mpc: 20, fee: 195 },
  physio: { dur: 52, mpc: 27, fee: 120 },
}

// Minimum clinically plausible first-appointment length (matches hero "15-20 min").
const FLOOR_MIN = 20
// Compressed slot scales with the clinician's ACTUAL current appointment,
// using each specialty's evidence-derived retained fraction (mpc/dur).
// Returns the EXACT (unrounded) floored value so throughput math is smooth;
// callers round only for the displayed compression value.
function compressedExact(currentDur: number, spec: string) {
  const s = specData[spec]
  // Multiply-first (no pre-computed ratio): keeps throughput math free of the
  // floating-point error that 30/75=0.4 introduces, so the patients/week figure
  // never wobbles by +-1. Unrounded, floored.
  return Math.max(FLOOR_MIN, (currentDur * s.mpc) / s.dur)
}

interface Props {
  id?: string
  variant?: 'pp' | 'pricing'
  noPaddingTop?: boolean
}

export function BusinessCaseCalculator({ id = 'pp-bcs', variant = 'pp', noPaddingTop = false }: Props) {
  const [spec, setSpec] = useState('ortho')
  const [dur, setDur] = useState(30)
  const [pw, setPw] = useState(10)
  const [fee, setFee] = useState(195)

  const mpcExact = compressedExact(dur, spec)
  const mpcDur = Math.round(mpcExact)          // DISPLAY value only

  // Cost: £49 base + overage
  const pm = Math.round(pw * 4.33)
  const overage = Math.max(0, pm - 8)
  const monthlyCost = 49 + overage * 10
  const annualCost = monthlyCost * 12
  const postTax = Math.round(monthlyCost * 0.78)

  // Completed patients (90% of total, 46 weeks)
  const completed = Math.round(pw * 46 * 0.9)

  // Profit improvement: £10/patient
  const annualProfit = completed * 10

  // Throughput
  const extraPerWeek = Math.floor(pw * (dur / mpcExact - 1))   // MATH uses exact
  const throughputRevenue = extraPerWeek * fee * 46

  // Total value
  const totalValue = throughputRevenue + annualProfit
  const roi = annualCost > 0 ? Math.round(totalValue / annualCost) : 0

  const handleSpecChange = useCallback((newSpec: string) => {
    const d = specData[newSpec]
    setSpec(newSpec)
    setDur(d.dur)
    setFee(d.fee)
  }, [])

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: 10,
    border: '1px solid var(--border-card)',
    borderRadius: 'var(--radius-button)',
    fontSize: 14,
    fontFamily: 'var(--font-body)',
    background: 'var(--bg-page)',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-muted)',
    marginBottom: 6,
  }

  return (
    <div className="section-inner" id={id} style={noPaddingTop ? { paddingTop: 0 } : undefined}>
      <div className="card" style={{ padding: 40 }}>
        <div className="overline-label">For your private practice</div>
        <h3 style={{
          fontFamily: 'var(--font-headline)',
          fontSize: 24,
          fontWeight: 400,
          letterSpacing: '-0.02em',
          marginBottom: 8,
        }}>
          Build your business case
        </h3>
        <p style={{ fontSize: 14, color: 'var(--text-body)', marginBottom: 24 }}>
          Adjust inputs to reflect your clinic. Figures modelled from published clinical evidence and task-level workflow analysis.
        </p>

        {/* Inputs */}
        <div className="bcs-input-grid">
          <div>
            <label style={labelStyle}>Specialty</label>
            <select
              value={spec}
              onChange={(e) => handleSpecChange(e.target.value)}
              style={inputStyle}
            >
              <option value="ortho">Orthopaedics</option>
              <option value="rheum">Rheumatology</option>
              <option value="sleep">Sleep medicine</option>
              <option value="gastro">Gastroenterology</option>
              <option value="physio">Physiotherapy</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Current appointment (min)</label>
            <input
              type="number"
              min={20}
              value={dur}
              onChange={(e) => setDur(Math.max(FLOOR_MIN, parseInt(e.target.value) || 30))}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Current avg patients per week</label>
            <select
              value={pw}
              onChange={(e) => setPw(parseInt(e.target.value))}
              style={inputStyle}
            >
              {[2, 4, 6, 8, 10, 12, 16, 20, 24, 30, 40].map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Consultation fee (£)</label>
            <input
              type="number"
              value={fee}
              onChange={(e) => setFee(parseInt(e.target.value) || 195)}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Output Row 1 */}
        <div className="bcs-output-grid" style={{ marginBottom: 16 }}>
          <div className="bcs-box">
            <div className="bcs-label">Appointment compression</div>
            <div className="bcs-value">
              {dur} &rarr; <span style={{ color: 'var(--brand-primary)' }}>{mpcDur}</span>{' '}
              <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>min</span>
            </div>
            <div className="bcs-explain">
              {variant === 'pricing'
                ? 'In a UCL analysis of history-taking consultations, information-gathering dominated, accounting for approximately two-thirds of doctor-patient dialogue (Manalastas et al., Patient Education and Counseling, 2021). MyPreConsult completes this before the appointment. Published RCT evidence: 28.7% consultation time reduction (Tao et al., Nature Medicine 2026, n=2,069; RCT conducted in China; indicative of effect magnitude, not NHS-validated).'
                : 'In a UCL analysis of history-taking consultations, information-gathering dominated, accounting for approximately two-thirds of doctor-patient dialogue (Manalastas et al., Patient Education and Counseling, 2021). Published RCT evidence: 28.7% consultation time reduction (Tao et al., Nature Medicine 2026, n=2,069; RCT conducted in China; indicative of effect magnitude, not NHS-validated).'}
            </div>
          </div>
          <div className="bcs-box">
            <div className="bcs-label">Additional patients per week</div>
            <div className="bcs-value">
              <span style={{ color: 'var(--brand-primary)' }}>+{extraPerWeek}</span>{' '}
              <span style={{ fontSize: 16, color: 'var(--text-muted)' }}>per week</span>
            </div>
            <div className="bcs-sub">If you choose to fill the freed slots</div>
            <div className="bcs-explain">
              {variant === 'pricing'
                ? 'Shorter slots mean more patients fit the same clinic hours. This is the Maximiser scenario. Alternatively, see the same patients and finish earlier (Lifestyle).'
                : 'Maximiser scenario. Alternatively, see the same patients and finish earlier (Lifestyle).'}
            </div>
          </div>
          <div className="bcs-box">
            <div className="bcs-label">Administration automation</div>
            <div className="bcs-value">£10<span className="stat-unit"> more</span></div>
            <div className="bcs-sub">Profitable per new patient</div>
            <div className="bcs-explain">
              Letters pre-populate from the interview, invitations can be automated, and forms pre-fill. Your administration cost per patient falls, net of the MyPreConsult per-patient cost.
            </div>
          </div>
        </div>

        {/* Output Row 2 */}
        <div className="bcs-output-grid">
          <div className="bcs-box">
            <div className="bcs-label">Your MyPreConsult cost</div>
            <div className="bcs-value">£{monthlyCost.toLocaleString()}/mo</div>
            <div className="bcs-sub">
              {overage > 0
                ? `£49 base + ${overage} extra at £10`
                : `All ${pm} patients within £49 base`}
            </div>
            <div className="bcs-explain">
              {variant === 'pricing'
                ? `Post-tax effective: ~£${postTax}/mo. Deductible business expense (75-81% of headline figure).`
                : `Post-tax effective: ~£${postTax}/mo. Deductible business expense.`}
            </div>
          </div>
          <div className="bcs-box">
            <div className="bcs-label">Total potential income uplift</div>
            <div className="bcs-value">+£{totalValue.toLocaleString()}</div>
            <div className="bcs-sub">
              Throughput £{throughputRevenue.toLocaleString()} + profit improvement £{annualProfit.toLocaleString()}
            </div>
            <div className="bcs-explain">
              {variant === 'pricing'
                ? `Throughput: additional patients at full consultation fee. Profit improvement: £10/patient from reduced admin (£15) + shorter room hire (£5) minus MPC per-patient cost (£10). 90% interview completion, 46 working weeks.`
                : `Throughput: ${extraPerWeek} extra/week at £${fee}. Profit: ${completed} completed at £10. 90% completion, 46 weeks.`}
            </div>
          </div>
          <div className="bcs-box highlight">
            <div className="bcs-label">Annual ROI</div>
            <div className="bcs-value">{roi}:1</div>
            <div className="bcs-sub">
              £{totalValue.toLocaleString()} value vs. £{annualCost.toLocaleString()} cost
            </div>
            <div className="bcs-explain">
              {variant === 'pricing'
                ? `Maximiser scenario. Without additional throughput (Lifestyle), the profit improvement alone is £${annualProfit.toLocaleString()} against £${annualCost.toLocaleString()} cost (${annualCost > 0 ? Math.round(annualProfit / annualCost) : 0}:1 ROI).`
                : `Maximiser scenario. Without throughput (Lifestyle): £${annualProfit.toLocaleString()} vs. £${annualCost.toLocaleString()} (${annualCost > 0 ? Math.round(annualProfit / annualCost) : 0}:1).`}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {variant === 'pricing'
            ? 'All prices exclude VAT. Throughput revenue assumes the clinician shortens slots and fills freed capacity. If you prefer the Lifestyle scenario (same patients, finish earlier), the profit improvement and admin savings still apply.'
            : 'All prices exclude VAT. Throughput revenue assumes shortened slots and filled capacity. Lifestyle scenario: profit improvement and admin savings still apply.'}
        </div>
      </div>
    </div>
  )
}

export default BusinessCaseCalculator
